/**
 * MANAGEMENT (14) / ONBOARDING — the screen's OWN i18n strings (FR default + AR-RTL). ADDITIVE to the
 * shell `../messages.ts`, which already declares `mgmt.onboarding.title` + `mgmt.onboarding.subtitle` and
 * the `nav.*` labels. This module registers ONLY the extra keys the table / provision form / lifecycle
 * dialog need — it never re-declares a shell key (registerMessages is `Object.assign`, so a duplicate would
 * silently shadow; we avoid that by namespacing every key below as `mgmt.onboarding.<new>`).
 *
 * Registration is idempotent and self-contained: `registerOnboardingMessages()` runs once at the screen's
 * module-load (imported for its side effect by OnboardingPage). It does NOT depend on the shell's
 * `onRegister()` having run, so the screen is hookup-free — exactly the per-namespace i18n idiom the
 * codebase uses (registerInventoryMessages / registerFinanceMessages).
 */
import { registerMessages } from "@/app";

const FR: Record<string, string> = {
  // table
  "mgmt.onboarding.new": "Provisionner un laboratoire",
  "mgmt.onboarding.search": "Rechercher (raison sociale, code…)",
  "mgmt.onboarding.filter.statut": "Statut",
  "mgmt.onboarding.filter.deploy": "Déploiement",
  "mgmt.onboarding.filter.all": "Tous",
  "mgmt.onboarding.col.reference": "Code",
  "mgmt.onboarding.col.name": "Laboratoire",
  "mgmt.onboarding.col.deploy": "Déploiement",
  "mgmt.onboarding.col.location": "Localisation",
  "mgmt.onboarding.col.contact": "Contact",
  "mgmt.onboarding.col.statut": "Statut",
  "mgmt.onboarding.col.created": "Créé le",
  "mgmt.onboarding.col.actions": "Actions",
  "mgmt.onboarding.empty.title": "Aucun laboratoire",
  "mgmt.onboarding.empty.desc": "Provisionnez le premier laboratoire de la plateforme.",
  "mgmt.onboarding.empty.filtered": "Aucun laboratoire ne correspond aux filtres.",
  "mgmt.onboarding.statut.actif": "Actif",
  "mgmt.onboarding.statut.suspendu": "Suspendu",

  // insight strip (derived from the loaded page — cross-lab répartition)
  "mgmt.onboarding.insight.title": "Répartition (page courante)",
  "mgmt.onboarding.insight.deploy": "Par classe de déploiement",
  "mgmt.onboarding.insight.deploy.aria": "Répartition des laboratoires par classe de déploiement",
  "mgmt.onboarding.insight.empty": "Aucun laboratoire à analyser sur cette page.",
  "mgmt.onboarding.insight.total": "Affichés",
  "mgmt.onboarding.insight.active": "Actifs",
  "mgmt.onboarding.insight.suspended": "Suspendus",
  "mgmt.onboarding.filter.wilaya": "Filtrer par wilaya",

  // lifecycle actions
  "mgmt.onboarding.action.suspend": "Suspendre",
  "mgmt.onboarding.action.reactivate": "Réactiver",
  "mgmt.onboarding.lifecycle.suspend.title": "Suspendre le laboratoire",
  "mgmt.onboarding.lifecycle.suspend.body":
    "Suspendre « {name} » ? Ses utilisateurs ne pourront plus se connecter tant qu'il reste suspendu.",
  "mgmt.onboarding.lifecycle.suspend.confirm": "Suspendre",
  "mgmt.onboarding.lifecycle.suspend.done": "Laboratoire suspendu.",
  "mgmt.onboarding.lifecycle.reactivate.title": "Réactiver le laboratoire",
  "mgmt.onboarding.lifecycle.reactivate.body":
    "Réactiver « {name} » ? Ses utilisateurs pourront de nouveau se connecter.",
  "mgmt.onboarding.lifecycle.reactivate.confirm": "Réactiver",
  "mgmt.onboarding.lifecycle.reactivate.done": "Laboratoire réactivé.",
  "mgmt.onboarding.lifecycle.motif": "Motif (journalisé)",
  "mgmt.onboarding.lifecycle.motif.placeholder": "Optionnel — tracé dans le journal d'audit",

  // provision form
  "mgmt.onboarding.provision.title": "Provisionner un laboratoire",
  "mgmt.onboarding.provision.lab": "Laboratoire",
  "mgmt.onboarding.provision.owner": "Propriétaire (premier compte)",
  "mgmt.onboarding.provision.reference": "Code laboratoire",
  "mgmt.onboarding.provision.raisonSociale": "Raison sociale",
  "mgmt.onboarding.provision.nomCommercial": "Nom commercial",
  "mgmt.onboarding.provision.deployClass": "Classe de déploiement",
  "mgmt.onboarding.provision.tenancyTier": "Mode de tenance",
  "mgmt.onboarding.provision.tenancy.partage": "Partagé",
  "mgmt.onboarding.provision.tenancy.dedie": "Dédié",
  "mgmt.onboarding.provision.wilaya": "Wilaya",
  "mgmt.onboarding.provision.commune": "Commune",
  "mgmt.onboarding.provision.telephone": "Téléphone",
  "mgmt.onboarding.provision.email": "Email du laboratoire",
  "mgmt.onboarding.provision.owner.prenom": "Prénom",
  "mgmt.onboarding.provision.owner.nom": "Nom",
  "mgmt.onboarding.provision.owner.email": "Email du propriétaire",
  "mgmt.onboarding.provision.owner.password": "Mot de passe initial",
  "mgmt.onboarding.provision.submit": "Provisionner",
  "mgmt.onboarding.provision.done": "Laboratoire « {name} » provisionné.",

  // shared form feedback
  "mgmt.onboarding.action.cancel": "Annuler",
  "mgmt.onboarding.action.confirm": "Confirmer",
  "mgmt.onboarding.field.required": "Tous les champs obligatoires (*) doivent être renseignés.",
  "mgmt.onboarding.field.invalidEmail": "Adresse email invalide.",
  "mgmt.onboarding.error.generic": "Une erreur est survenue. Réessayez.",
};

const AR: Record<string, string> = {
  "mgmt.onboarding.new": "تجهيز مختبر",
  "mgmt.onboarding.search": "بحث (الاسم القانوني، الرمز…)",
  "mgmt.onboarding.filter.statut": "الحالة",
  "mgmt.onboarding.filter.deploy": "النشر",
  "mgmt.onboarding.filter.all": "الكل",
  "mgmt.onboarding.col.reference": "الرمز",
  "mgmt.onboarding.col.name": "المختبر",
  "mgmt.onboarding.col.deploy": "النشر",
  "mgmt.onboarding.col.location": "الموقع",
  "mgmt.onboarding.col.contact": "الاتصال",
  "mgmt.onboarding.col.statut": "الحالة",
  "mgmt.onboarding.col.created": "أُنشئ في",
  "mgmt.onboarding.col.actions": "إجراءات",
  "mgmt.onboarding.empty.title": "لا توجد مختبرات",
  "mgmt.onboarding.empty.desc": "جهّز أول مختبر على المنصة.",
  "mgmt.onboarding.empty.filtered": "لا يوجد مختبر يطابق عوامل التصفية.",
  "mgmt.onboarding.statut.actif": "نشط",
  "mgmt.onboarding.statut.suspendu": "موقوف",

  "mgmt.onboarding.insight.title": "التوزيع (الصفحة الحالية)",
  "mgmt.onboarding.insight.deploy": "حسب فئة النشر",
  "mgmt.onboarding.insight.deploy.aria": "توزيع المختبرات حسب فئة النشر",
  "mgmt.onboarding.insight.empty": "لا توجد مختبرات للتحليل في هذه الصفحة.",
  "mgmt.onboarding.insight.total": "المعروضة",
  "mgmt.onboarding.insight.active": "النشطة",
  "mgmt.onboarding.insight.suspended": "الموقوفة",
  "mgmt.onboarding.filter.wilaya": "تصفية حسب الولاية",

  "mgmt.onboarding.action.suspend": "إيقاف",
  "mgmt.onboarding.action.reactivate": "إعادة تفعيل",
  "mgmt.onboarding.lifecycle.suspend.title": "إيقاف المختبر",
  "mgmt.onboarding.lifecycle.suspend.body":
    "إيقاف « {name} »؟ لن يتمكن مستخدموه من تسجيل الدخول طالما بقي موقوفًا.",
  "mgmt.onboarding.lifecycle.suspend.confirm": "إيقاف",
  "mgmt.onboarding.lifecycle.suspend.done": "تم إيقاف المختبر.",
  "mgmt.onboarding.lifecycle.reactivate.title": "إعادة تفعيل المختبر",
  "mgmt.onboarding.lifecycle.reactivate.body":
    "إعادة تفعيل « {name} »؟ سيتمكن مستخدموه من تسجيل الدخول مجددًا.",
  "mgmt.onboarding.lifecycle.reactivate.confirm": "إعادة تفعيل",
  "mgmt.onboarding.lifecycle.reactivate.done": "تمت إعادة تفعيل المختبر.",
  "mgmt.onboarding.lifecycle.motif": "السبب (مسجَّل)",
  "mgmt.onboarding.lifecycle.motif.placeholder": "اختياري — يُسجَّل في سجل التدقيق",

  "mgmt.onboarding.provision.title": "تجهيز مختبر",
  "mgmt.onboarding.provision.lab": "المختبر",
  "mgmt.onboarding.provision.owner": "المالك (الحساب الأول)",
  "mgmt.onboarding.provision.reference": "رمز المختبر",
  "mgmt.onboarding.provision.raisonSociale": "الاسم القانوني",
  "mgmt.onboarding.provision.nomCommercial": "الاسم التجاري",
  "mgmt.onboarding.provision.deployClass": "فئة النشر",
  "mgmt.onboarding.provision.tenancyTier": "نمط الاستضافة",
  "mgmt.onboarding.provision.tenancy.partage": "مشترك",
  "mgmt.onboarding.provision.tenancy.dedie": "مخصص",
  "mgmt.onboarding.provision.wilaya": "الولاية",
  "mgmt.onboarding.provision.commune": "البلدية",
  "mgmt.onboarding.provision.telephone": "الهاتف",
  "mgmt.onboarding.provision.email": "البريد الإلكتروني للمختبر",
  "mgmt.onboarding.provision.owner.prenom": "الاسم",
  "mgmt.onboarding.provision.owner.nom": "اللقب",
  "mgmt.onboarding.provision.owner.email": "البريد الإلكتروني للمالك",
  "mgmt.onboarding.provision.owner.password": "كلمة المرور الأولية",
  "mgmt.onboarding.provision.submit": "تجهيز",
  "mgmt.onboarding.provision.done": "تم تجهيز المختبر « {name} ».",

  "mgmt.onboarding.action.cancel": "إلغاء",
  "mgmt.onboarding.action.confirm": "تأكيد",
  "mgmt.onboarding.field.required": "يجب ملء جميع الحقول الإلزامية (*).",
  "mgmt.onboarding.field.invalidEmail": "عنوان بريد إلكتروني غير صالح.",
  "mgmt.onboarding.error.generic": "حدث خطأ. أعد المحاولة.",
};

let registered = false;
/** Register the onboarding screen's i18n (idempotent). Called at the screen's module-load. */
export function registerOnboardingMessages(): void {
  if (registered) return;
  registered = true;
  registerMessages("fr", FR);
  registerMessages("ar", AR);
}
